import { Language, localeFor } from '../../i18n';
import type { AstroText, PairNote } from '../../i18n/types';

import { Aspect, Chart, PointId } from './chart';
import { BodyId, inSign, RULERS, signOf } from './zodiac';

export const astroText = (language: Language): AstroText =>
    localeFor(language).astro;

export interface Position {
    id: PointId;
    name: string;
    sign: string;
    signId: ReturnType<typeof signOf>;
    degree: number;
    minute: number;
    retrograde: boolean;
    house?: number;
}

export interface Placement extends Position {
    /** What the planet does in its sign: written one by one, or its theme coloured by the sign. */
    text: string;
    /** What it does in its house, as a sentence. */
    houseText?: string;
}

export interface BigThree {
    kind: 'sun' | 'moon' | 'rising';
    position: Position;
    text: string;
}

export interface AspectLine {
    type: Aspect['type'];
    typeName: string;
    orb: number;
    /** "Venus flows easily with Jupiter". */
    title: string;
    /** What the pair means at this kind of angle; empty when no note is written. */
    note: string;
}

/** The planet that rules the rising sign, where it sits, and what that says. */
export interface Ruler {
    position: Position;
    text: string;
}

export interface ChartReading {
    text: AstroText;
    bigThree: BigThree[];
    placements: Placement[];
    angles: Position[];
    /** What the Midheaven's sign says. */
    angleTexts: { mc?: string };
    aspects: AspectLine[];
    ruler?: Ruler;
}

/** The order that keys a pair note: `venus-mars`, `moon-asc`. */
const ORDER: PointId[] = [
    'sun',
    'moon',
    'mercury',
    'venus',
    'mars',
    'jupiter',
    'saturn',
    'uranus',
    'neptune',
    'pluto',
    'asc',
    'mc',
];

const KIND: Record<Aspect['type'], keyof PairNote> = {
    conjunction: 'fused',
    sextile: 'easy',
    trine: 'easy',
    square: 'hard',
    opposition: 'hard',
};

const pairNote = (
    text: AstroText,
    a: PointId,
    b: PointId,
    type: Aspect['type']
): string => {
    const [first, second] =
        ORDER.indexOf(a) <= ORDER.indexOf(b) ? [a, b] : [b, a];
    return text.pairs[`${first}-${second}`]?.[KIND[type]] ?? '';
};

const position = (
    text: AstroText,
    id: PointId,
    lon: number,
    extra: { retrograde?: boolean; house?: number } = {}
): Position => {
    const signId = signOf(lon);
    return {
        id,
        name: text.bodies[id].name,
        sign: text.signs[signId].name,
        signId,
        ...inSign(lon),
        retrograde: extra.retrograde ?? false,
        house: extra.house,
    };
};

export const interpretChart = (
    chart: Chart,
    language: Language
): ChartReading => {
    const text = astroText(language);
    const lonOf = (id: BodyId) => chart.bodies.find((b) => b.id === id)!.lon;

    const placements: Placement[] = chart.bodies.map((b) => {
        const pos = position(text, b.id, b.lon, {
            retrograde: b.retrograde,
            house: b.house,
        });
        return {
            ...pos,
            text:
                text.inSign[b.id]?.[pos.signId] ??
                `${text.bodies[b.id].theme} ${text.signs[pos.signId].style}.`,
            houseText: b.house
                ? (text.inHouse[b.id]?.[b.house - 1] ??
                  text.houses[b.house - 1])
                : undefined,
        };
    });

    const bigThree: BigThree[] = [
        {
            kind: 'sun',
            position: position(text, 'sun', lonOf('sun')),
            text: text.signs[signOf(lonOf('sun'))].sun,
        },
        {
            kind: 'moon',
            position: position(text, 'moon', lonOf('moon')),
            text: text.signs[signOf(lonOf('moon'))].moon,
        },
    ];
    const angles: Position[] = [];
    if (chart.angles) {
        const rising = position(text, 'asc', chart.angles.asc);
        bigThree.push({
            kind: 'rising',
            position: rising,
            text: text.signs[rising.signId].rising,
        });
        angles.push(rising, position(text, 'mc', chart.angles.mc));
    }

    const aspects: AspectLine[] = chart.aspects.map((a) => ({
        type: a.type,
        typeName: text.aspects[a.type].name,
        orb: a.orb,
        title: `${text.bodies[a.a].name} ${text.aspects[a.type].phrase} ${text.bodies[a.b].name}`,
        note: pairNote(text, a.a, a.b, a.type),
    }));

    const angleTexts: ChartReading['angleTexts'] = {};
    let ruler: Ruler | undefined;
    if (chart.angles) {
        angleTexts.mc = text.midheaven[signOf(chart.angles.mc)];
        const risingSign = signOf(chart.angles.asc);
        const planet = placements.find((p) => p.id === RULERS[risingSign]);
        if (planet) {
            // The Sun and Moon have their long sign text in `signs`; the others have their own `inSign` line.
            const inSignText =
                planet.id === 'sun'
                    ? text.signs[planet.signId].sun
                    : planet.id === 'moon'
                      ? text.signs[planet.signId].moon
                      : planet.text;
            const where = [inSignText, planet.houseText]
                .filter(Boolean)
                .join(' ');
            const intro = text.ruler.intro
                .replace('{planet}', planet.name)
                .replace('{rising}', text.signs[risingSign].name)
                .replace('{sign}', planet.sign);
            ruler = { position: planet, text: `${intro} ${where}` };
        }
    }

    return { text, bigThree, placements, angles, angleTexts, aspects, ruler };
};
